import React, { useState, useRef, useEffect } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import ClosedCaptionOffIcon from "@mui/icons-material/ClosedCaptionOff";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import {
  Button,
  Checkbox,
  CircularProgress,
  Slider,
  Switch,
  MenuItem,
  Select,
  SelectChangeEvent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import useStyles from "./style";
import {
  Actor,
  BackgroundSettings,
  CustomPhotoAvatar,
  CustomVoice,
  ElevenLabsSettings,
  ElevenLabsVoice,
  HeygenTemplate,
  HeygenVoice,
  TextVoiceSettings,
  VideoPage,
  VoiceEmotion,
  VoiceLocale,
} from "../../context/types";
import {
  addHeygenVoice,
  addVoiceToActor,
  elevenLabsFF,
  getCustomVoicesByWorkspace,
  getElevenLabsVoice,
  getElevenLabsVoices,
  getVideoPageById,
  listAllHeygenVoices,
  removeActorCustomVoice,
  updateActorReactionSettings,
  updateVoiceConfiguration,
  updateActorSettings,
  updateHeygenVoice,
  updateVoiceSettings,
  getVoiceLocales,
  applyTemplateToActor,
  getCustomPhotoAvatarsByWorkspace,
  generateVoicePreview,
} from "../../client/api";
import { TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useParams } from "react-router-dom";
import { THEME_COLORS } from "../../context/constants";
import { useUserContext } from "../../context/UserContext";
import VoiceSelectionModal from "./components/HeygenVoicesDialog";
import ElevenLabsVoicesDialog from "./components/ElevenLabsVoicesDialog";
import { canUseFeatures } from "../../utils/utils";
import { ZoomIn } from "@mui/icons-material";
import { useNotification } from "../../context/NotificationContext";
import { useProjectContext } from "../../context/ProjectContext";
import BackgroundSettingsDialog from "../BackgroundSettingsDialog";
import Message from "../Message";
import ActorTemplateButton from "../ActorTemplateButton";
import CloseIcon from "@mui/icons-material/Close";

interface VoicePlayerProps {
  actor: Actor;
  actorId: string;
  audioSrc: string;
  isSelected: boolean;
  onSelect: (actorId: string, isCheckboxClick?: boolean) => void;
  onDeselect?: (actorId: string) => void;
  setVideoPage: (videoPage: VideoPage) => void;
  ensureActorsRegistered: any;
  scriptType: "text" | "speech";
  selectedVideoActors: string[];
  actors: Actor[];
  dimension: { width: number; height: number };
  setDimension: any;
  setActors: any;
  handleApplyTemplate?: any;
  onSwitchToEnhancedMode?: (actor: Actor) => void;
  customPhotoAvatars?: CustomPhotoAvatar[];
}

const VoicePlayer: React.FC<VoicePlayerProps> = ({
  actor,
  actorId,
  audioSrc,
  isSelected,
  onSelect,
  onDeselect,
  setVideoPage,
  ensureActorsRegistered,
  scriptType,
  selectedVideoActors,
  actors,
  dimension,
  setDimension,
  setActors,
  handleApplyTemplate,
  onSwitchToEnhancedMode,
  customPhotoAvatars: customPhotoAvatarsProp,
}) => {
  const classes = useStyles();
  const { currentWorkspace, user } = useUserContext();
  const { cachedCustomPhotoAvatars, setCachedCustomPhotoAvatars, currentScript } = useProjectContext();
  const audioRef = useRef<HTMLAudioElement>(null); //actor audio
  const currentAudioRef = useRef<HTMLAudioElement | null>(null); // voice list audio
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [availableLocales, setAvailableLocales] = useState<VoiceLocale[]>([]);
  const [voiceSettings, setVoiceSettings] = useState(
    actor?.voiceConfiguration ?? {
      stability: 0.1,
      similarity_boost: 0.3,
      style: 0.2,
      use_speaker_boost: false,
    }
  );
  const [voiceSelection, setVoiceSelection] = useState<"heygen" | "elevenLabs">(
    actor?.elevenLabsVoiceId ? "elevenLabs" : "heygen"
  );
  const [customVoices, setCustomVoices] = useState<ElevenLabsVoice[]>([]);
  const [heygenVoices, setHeygenVoices] = useState<HeygenVoice[]>([]);
  const [heygenCustomVoices, setHeygenCustomVoices] = useState<CustomVoice[]>([]);
  const [heygenCustomVoicesLoading, setHeygenCustomVoicesLoading] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(
    actor?.elevenLabsVoiceId ?? null
  );
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedHeygenVoiceId, setSelectedHeygenVoiceId] = useState<
    string | null
  >(actor?.heygenVoiceId ?? null);
  const [heygenVoiceModalOpen, setHeygenVoiceModalOpen] = useState(false);
  const [elevenLabsVoiceModalOpen, setElevenLabsVoiceModalOpen] = useState(false);
  const [elevenLabsEnabled, setElevenLabsEnabled] = useState(false);
  const { videoPageId } = useParams();
  const { addNotification } = useNotification();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(actor.title || "");
  const [isBackgroundSettingsOpen, setIsBackgroundSettingsOpen] =
    useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    const audioUrl = audioRef.current.src;
    const isWavFile = audioUrl.endsWith(".wav");

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    if (isWavFile) {
      try {
        // Fetch the WAV file as an ArrayBuffer
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();

        // Decode the audio using Web Audio API
        const audioContext = new window.AudioContext();
        const decodedData = await audioContext.decodeAudioData(arrayBuffer);

        // Create a buffer source and play it
        const source = audioContext.createBufferSource();
        source.buffer = decodedData;
        source.connect(audioContext.destination);
        source.start(0);

        source.onended = () => {
          setIsPlaying(false); // Reset play state when the audio finishes
        };

        setIsPlaying(true);
      } catch (error) {
        console.error("Error processing WAV file:", error);
      }
    } else {
      // For non-WAV files, play as usual
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Error playing audio:", error);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Fetch custom photo avatars if not cached in context (need full workspace list, not just page avatars)
  useEffect(() => {
    const loadCustomPhotoAvatars = async () => {
      if (!cachedCustomPhotoAvatars && currentWorkspace) {
        try {
          const avatars = await getCustomPhotoAvatarsByWorkspace(currentWorkspace._id);
          setCachedCustomPhotoAvatars(avatars);
        } catch (error) {
          console.error("Error fetching custom photo avatars:", error);
        }
      }
    };
    loadCustomPhotoAvatars();
  }, [cachedCustomPhotoAvatars, currentWorkspace, setCachedCustomPhotoAvatars]);

  // Use cached context avatars for the check (full workspace list), falling back to prop
  const allCustomPhotoAvatars = cachedCustomPhotoAvatars || customPhotoAvatarsProp;

  // Check if this actor is a photo avatar that comes from Avatar Lab
  const isAvatarLabActor = allCustomPhotoAvatars?.some(
    (avatar) =>
      !!avatar.generation_id && actor.preview_image_url?.includes(avatar.generation_id)
  );

  const handleSwitchToEnhanced = () => {
    if (onSwitchToEnhancedMode && isAvatarLabActor) {
      onSwitchToEnhancedMode(actor);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleVoiceSettings = async () => {
    if (!videoPageId) return;
    if (!actor.elevenLabsVoiceId) {
      setLoading(true);
      try {
        //const updatedVideoPage = await getVideoPageById(videoPageId);
        //setVideoPage(updatedVideoPage);
      } catch (error) {
        console.error("Error adding voice to actor:", error);
      } finally {
        setLoading(false);
      }
    }
    setShowSettings(!showSettings);
  };

  const updateVideoPage = async () => {
    if (!videoPageId) return;
    const updatedVideoPage = await getVideoPageById(videoPageId);
    setVideoPage(updatedVideoPage);
  };

  const handleVoiceSelectionChange = async (
    event: SelectChangeEvent<string | null>
  ) => {
    const selectedId = event.target.value as string;
    setSelectedVoiceId(selectedId);
    await handleAddCustomVoice(selectedId);
  };

  const handleTemplateChange = async (
    templateId: string,
    actorIds: string[],
    variables: any
  ) => {
    if (!currentWorkspace || !videoPageId || !user?.email) return;

    try {
      await handleApplyTemplate(templateId, actorIds, variables);

      //addNotification("Template updated successfully", "success");
    } catch (error) {
      console.error("Failed to update template:", error);
      addNotification("Failed to update template", "error");
      throw error;
    }
  };

  const handleAddCustomVoice = async (selectedId: string) => {
    if (!currentWorkspace || !selectedId || !videoPageId || !user) return;
    const selectedVoice = customVoices.find(
      (voice) => voice.voice_id === selectedId
    );
    if (selectedVoice) {
      audioRef.current!.src = selectedVoice.preview_url; // Set preview URL to audio element
      const registeredIds = await ensureActorsRegistered([actor]);
      if (registeredIds.length) {
        await addVoiceToActor(
          registeredIds[0],
          selectedVoice.voice_id,
          selectedVoice.name,
          currentWorkspace?._id,
          videoPageId,
          user?.email
        );
      }
      if (selectedVoice.settings) {
        setVoiceSettings(selectedVoice.settings);
      }
      await updateVideoPage();
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
  };

  const handleTitleSave = async () => {
    setIsEditingTitle(false);
    if (currentTitle !== actor.title) {
      try {
        await updateActorSettings(
          currentWorkspace?._id || "",
          videoPageId || "",
          actor._id,
          { title: currentTitle }
        );
        // Update the local actor state if needed
      } catch (error) {
        console.error("Failed to update title:", error);
      }
    }
  };

  const handleCaptionToggle = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const captionValue = e.target.checked;
    try {
      const updatedActor = await updateActorSettings(
        currentWorkspace?._id || "",
        videoPageId || "",
        actor._id,
        { caption: captionValue }
      );
      setActors((prevActors: Actor[]) =>
        prevActors.map((actor) =>
          actor._id === updatedActor._id ? updatedActor : actor
        )
      );

      // Update the local actor state if needed
    } catch (error) {
      console.error("Failed to update caption:", error);
    }
  };

  /*const handleDeselect = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the onSelect event
    onDeselect?.(actor.avatar_id);
  };

  const handleHeygenVoiceSelectionChange = async (
    event: SelectChangeEvent<string | null>
  ) => {
    const selectedId = event?.target.value as string;
    await handleAddHeygenVoice(selectedId);
  };*/

  //to do
  /*const handleAddHeygenVoice = async (selectedId: string) => {
    if (!currentWorkspace || !selectedId || !videoPageId) return;
    setSelectedHeygenVoiceId(selectedId);
    const selectedVoice = heygenVoices.find(
      (voice) => voice.voice_id === selectedId
    );
    if (selectedVoice) {
      audioRef.current!.src = selectedVoice.preview_audio_url; // Set preview URL to audio element
      const registeredIds = await ensureActorsRegistered([actor]);
      if (registeredIds.length) {
        await addHeygenVoice(
          currentWorkspace._id,
          registeredIds[0],
          selectedId,
          selectedVoice.name,
          selectedVoice.preview_audio_url,
          videoPageId
        );
      }
      await updateVideoPage();
    }
  };*/

  const handleSettingChange = async (key: string, value: any) => {
    const updatedSettings = { ...voiceSettings, [key]: value };
    setVoiceSettings(updatedSettings);
    if (!user?.email) return;
    // Update voice configuration in the database
    try {
      await updateVoiceConfiguration(actorId, updatedSettings, user?.email);
      if (actor.elevenLabsVoiceId) {
        const voice = await getElevenLabsVoice(
          actor.elevenLabsVoiceId,
          user?.email
        );
        audioRef.current!.src = voice.preview_url; // Set preview URL to audio element
        audioRef.current!.play();
      }
    } catch (error) {
      console.error("Error updating voice configuration:", error);
    }
  };

  const handleBackgroundSettingsClick = () => {
    setIsBackgroundSettingsOpen(true);
  };

  const handleSaveBackgroundSettings = async (settings: BackgroundSettings) => {
    if (!currentWorkspace || !videoPageId || !user?.email) return;

    try {
      // Call API to save background settings
      await updateActorReactionSettings(
        actor._id,
        currentWorkspace._id,
        videoPageId,
        {
          avatarScale: settings.scale,
          backgroundAssetId: settings.backgroundAssetId,
          avatarOffset: settings.selectedAvatarOffset,
          backgroundFit: settings.backgroundFit,
          backgroundPlayStyle: settings.backgroundPlayStyle,
        }
      );

      if (settings.selectedDimension) {
        setDimension(settings.selectedDimension);
      }

      addNotification("Background settings saved successfully", "success");
    } catch (error) {
      console.error("Failed to save background settings:", error);
      addNotification("Failed to save background settings", "error");
    }
  };

  useEffect(() => {
    const loadLocales = async () => {
      try {
        const locales = await getVoiceLocales();
        setAvailableLocales(locales);
      } catch (error) {
        console.error("Failed to load voice locales:", error);
        // Fallback to basic locales if API fails
        setAvailableLocales([
          {
            value: "en-US",
            label: "English (US)",
            language: "English",
            languageCode: "en-US",
          },
          {
            value: "pt-BR",
            label: "Portuguese (Brazil)",
            language: "Portuguese",
            languageCode: "pt-BR",
          },
          // Add other fallback locales as needed
        ]);
      }
    };

    loadLocales();
  }, []);

  useEffect(() => {
    const fetchVoices = async () => {
      if (!user?.email) return;
      try {
        const response = await elevenLabsFF();
        const isEnabled = response === "true";
        setElevenLabsEnabled(isEnabled);
        if (isEnabled) {
          const voices = await getElevenLabsVoices(user?.email);
          setCustomVoices(voices);
        }
      } catch (error) {
        console.error("Error fetching Eleven Labs voices:", error);
      }
    };
    const fetchHeygenVoices = async () => {
      try {
        const response = await listAllHeygenVoices();
        setHeygenVoices(response.data);
      } catch (error) {
        console.error("Error fetching Heygen voices:", error);
      }
    };
    fetchVoices();
    fetchHeygenVoices();
  }, [user]);

  // Workspace-scoped custom (voice-design) voices. Kept separate so the parent
  // can resolve names and audio for voices that aren't in the v3 library list.
  const fetchHeygenCustomVoices = async () => {
    if (!currentWorkspace?._id) return;
    try {
      setHeygenCustomVoicesLoading(true);
      const response = await getCustomVoicesByWorkspace(currentWorkspace._id);
      setHeygenCustomVoices(response.data || []);
    } catch (error) {
      console.error("Error fetching custom Heygen voices:", error);
    } finally {
      setHeygenCustomVoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchHeygenCustomVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?._id]);

  /*useEffect(() => {
    if (scriptType === "speech" && elevenLabsEnabled) {
      setVoiceSelection("elevenLabs");
    }
  }, [scriptType, elevenLabsEnabled]);*/

  const playAudio = (url: string) => {
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0; // Reset to the beginning
    }

    // Create and play new audio
    const audio = new Audio(url);
    currentAudioRef.current = audio;

    audio.play().catch((error) => {
      console.error("Error playing audio:", error);
    });
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0; // Reset to the beginning
      currentAudioRef.current = null; // Clear reference
    }
  };

  const handleHearPreview = async () => {
    if (!selectedHeygenVoiceId) {
      addNotification("Please select a voice first", "error");
      return;
    }

    const scriptText = [
      currentScript?.headline || "",
      currentScript?.body || "",
      currentScript?.cta || "",
    ]
      .filter(Boolean)
      .join("\n");

    if (!scriptText.trim()) {
      addNotification("Please write a script first to hear the audio preview", "error");
      return;
    }

    try {
      setIsLoadingPreview(true);
      const voiceSettings = actor.voice_settings as TextVoiceSettings | undefined;
      const response = await generateVoicePreview({
        voice_id: selectedHeygenVoiceId,
        text: scriptText,
        settings: {
          pitch: String(voiceSettings?.pitch ?? 0),
          speed: String(voiceSettings?.speed ?? 1),
        },
      });

      if (response.data?.audio_url) {
        playAudio(response.data.audio_url);
      }
    } catch (error: any) {
      // HeyGen's /v3/voices/speech only supports voices on the starfish engine.
      // For voices that don't (custom designed voices, some library voices),
      // fall back to the voice's static preview_audio_url so the user still
      // hears the voice — they just won't hear it reading their script.
      const message: string =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.message ||
        "";
      const isStarfishUnsupported = /starfish/i.test(message) && /not supported/i.test(message);
      if (isStarfishUnsupported) {
        const fallbackUrl =
          heygenCustomVoices.find((v) => v.voice_id === selectedHeygenVoiceId)?.preview_audio_url ||
          heygenVoices.find((v) => v.voice_id === selectedHeygenVoiceId)?.preview_audio_url ||
          null;
        if (fallbackUrl) {
          playAudio(fallbackUrl);
          addNotification(
            "This voice doesn't support text-to-speech preview. Playing the voice's original sample instead.",
            "info"
          );
        } else {
          addNotification(
            "This voice doesn't support text-to-speech preview and has no sample audio.",
            "error"
          );
        }
      } else {
        console.error("Error generating voice preview:", error);
        addNotification(
          message || "Failed to generate voice preview",
          "error"
        );
      }
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    event.stopPropagation(); // Still prevent event bubbling
    onSelect(actor.avatar_id, true); // Pass true to indicate checkbox click
  };

  const handleHeygenSettingChange = async <K extends keyof TextVoiceSettings>(
    key: K,
    value: TextVoiceSettings[K]
  ) => {
    try {
      if (!actor.voice_settings || actor.voice_settings.type !== "text") return;

      const currentSettings = actor.voice_settings as TextVoiceSettings;
      const updatedSettings: TextVoiceSettings = {
        ...currentSettings,
        [key]: value,
      };

      // Update local state
      setActors((prevActors: Actor[]) =>
        prevActors.map((a) =>
          a._id === actor._id ? { ...a, voice_settings: updatedSettings } : a
        )
      );

      // Save to backend
      await updateVoiceSettings(
        currentWorkspace?._id || "",
        videoPageId || "",
        actor._id,
        updatedSettings
      );

      // Play preview if available
      if (actor.heygenVoiceAudioUrl) {
        audioRef.current!.src = actor.heygenVoiceAudioUrl;
        audioRef
          .current!.play()
          .catch((e) => console.error("Preview play error:", e));
      }
    } catch (error) {
      console.error("Error updating Heygen voice settings:", error);
      addNotification("Failed to update voice settings", "error");
    }
  };

  const handleHeygenElevenLabsSettingChange = async <
    K extends keyof ElevenLabsSettings
  >(
    key: K,
    value: ElevenLabsSettings[K]
  ) => {
    try {
      if (
        !actor.voice_settings ||
        actor.voice_settings.type !== "text" ||
        !actor.voice_settings.elevenlabs_settings
      )
        return;

      const currentSettings = actor.voice_settings as TextVoiceSettings;

      // Create default settings if they don't exist
      const currentElevenLabsSettings = currentSettings.elevenlabs_settings ?? {
        model: "eleven_monolingual_v1", // Default model
        similarity_boost: 0.5,
        stability: 0.5,
        style: 0.5,
      };

      const updatedSettings: TextVoiceSettings = {
        ...currentSettings,
        elevenlabs_settings: {
          ...currentElevenLabsSettings, // Use the default-initialized settings
          [key]: value,
        },
      };

      // Update local state
      setActors((prevActors: Actor[]) =>
        prevActors.map((a) =>
          a._id === actor._id ? { ...a, voice_settings: updatedSettings } : a
        )
      );

      // Save to backend
      await updateVoiceSettings(
        currentWorkspace?._id || "",
        videoPageId || "",
        actor._id,
        updatedSettings
      );

      // Play preview if available
      if (actor.heygenVoiceAudioUrl) {
        audioRef.current!.src = actor.heygenVoiceAudioUrl;
        audioRef
          .current!.play()
          .catch((e) => console.error("Preview play error:", e));
      }
    } catch (error) {
      console.error("Error updating ElevenLabs settings:", error);
      addNotification("Failed to update ElevenLabs settings", "error");
    }
  };

  const handleAddHeygenVoice = async (
    selectedId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => {
    if (!currentWorkspace || !selectedId || !videoPageId) return;

    try {
      const libraryMatch = heygenVoices.find((v) => v.voice_id === selectedId);
      const customMatch = heygenCustomVoices.find((v) => v.voice_id === selectedId);
      const selectedVoice = libraryMatch ||
        (customMatch
          ? {
              voice_id: customMatch.voice_id,
              name: customMatch.name,
              preview_audio_url: customMatch.preview_audio_url,
            }
          : meta
            ? { voice_id: selectedId, name: meta.name, preview_audio_url: meta.preview_audio_url }
            : null);

      if (!selectedVoice) return;

      // Create new voice settings
      const newSettings: TextVoiceSettings = {
        type: "text",
        voice_id: selectedId,
        input_text: "", // Will be set when generating
        speed: 1.0,
        pitch: 0,
      };

      // Check if actor has a template with voice_id variable and update it
      let updatedHeygenTemplate = actor.heygenTemplate;
      const templateVariables = actor.heygenTemplate?.variables;
      if (templateVariables) {
        // Find the voice_id variable key (could be "voice_id" or any variable with type "voice")
        const voiceVariableKey = Object.keys(templateVariables).find((key) => {
          const variable = templateVariables[key];
          return variable?.type === "voice" || key.toLowerCase().includes("voice");
        });

        const voiceVariable = voiceVariableKey ? templateVariables[voiceVariableKey] : undefined;
        if (voiceVariableKey && voiceVariable && actor.heygenTemplate) {
          updatedHeygenTemplate = {
            ...actor.heygenTemplate,
            variables: {
              ...templateVariables,
              [voiceVariableKey]: {
                ...voiceVariable,
                properties: {
                  ...voiceVariable.properties,
                  voice_id: selectedId,
                },
              },
            },
          };
        }
      }

      // Update local state
      setSelectedHeygenVoiceId(selectedId);
      setActors((prevActors: Actor[]) =>
        prevActors.map((a) =>
          a._id === actor._id
            ? {
                ...a,
                heygenVoiceId: selectedId,
                heygenVoiceAudioUrl: selectedVoice.preview_audio_url ?? undefined,
                voice_settings: newSettings,
                ...(updatedHeygenTemplate && { heygenTemplate: updatedHeygenTemplate }),
              }
            : a
        )
      );

      // Save to backend
      await updateHeygenVoice(
        currentWorkspace._id,
        videoPageId,
        actor._id,
        selectedId,
        selectedVoice.preview_audio_url ?? undefined,
        updatedHeygenTemplate // Pass updated template to save voice_id variable
      );

      // Update voice settings
      await updateVoiceSettings(
        currentWorkspace._id,
        videoPageId,
        actor._id,
        newSettings
      );

      // Play preview
      if (selectedVoice.preview_audio_url) {
        audioRef.current!.src = selectedVoice.preview_audio_url;
        audioRef
          .current!.play()
          .catch((e) => console.error("Preview play error:", e));
      }
    } catch (error) {
      console.error("Error adding Heygen voice:", error);
      addNotification("Failed to add Heygen voice", "error");
    }
  };

  useEffect(() => {
    const removeActorVoice = async () => {
      if (!currentWorkspace || !videoPageId) return;
      try {
        if (actor._id && actor.elevenLabsVoiceId) {
          await removeActorCustomVoice(
            actor._id,
            currentWorkspace?._id,
            videoPageId
          );
        }
      } catch (e: any) {
        console.error("Error deleting custom voice");
      }
    };
    if (voiceSelection === "heygen") {
      removeActorVoice();
      audioRef.current!.src = actor.preview_video_url; // Set preview URL to audio element
    } else if (selectedVoiceId) {
      handleAddCustomVoice(selectedVoiceId);
    }
  }, [voiceSelection]);

  useEffect(() => {
    audioRef.current!.src = audioSrc;
  }, [audioSrc]);

  return (
    <div
      onClick={() => onSelect(actor.avatar_id)}
      style={{ position: "relative" }}
    >
      <div style={{}}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {isEditingTitle ? (
              <TextField
                value={currentTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleSave}
                onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                autoFocus
                size="small"
                sx={{
                  ml: 1,
                  "& .MuiInputBase-root": {
                    height: 32,
                    fontSize: "0.875rem",
                  },
                  width: "30%",
                }}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  ml: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: currentTitle
                    ? THEME_COLORS.darkYellow
                    : THEME_COLORS.darkGray,
                  /*border: `1px solid ${THEME_COLORS.darkYellow}`,*/
                  padding: "2px 8px",
                  borderRadius: "10px",
                  width: "35%",
                }}
                onClick={() => setIsEditingTitle(true)}
              >
                {currentTitle || "No title"}
                <EditIcon fontSize="small" sx={{ ml: 0.5, width: "12px" }} />
              </Typography>
            )}
          </div>
          <div style={{ marginBottom: "6px" }}>
            {actor.heygenTemplate && !actor.isTemplateActor && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: THEME_COLORS.darkYellow,
                  fontSize: "12px",
                  mb: 0.5,
                }}
              >
                template applied
              </Typography>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {/* Checkbox */}
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxClick}
                className={classes.checkbox}
                onClick={(e: React.MouseEvent) => e.stopPropagation()} // Add this to handle click events too
              />

              {/* Avatar Thumbnail */}
              <img
                src={actor.preview_image_url}
                alt={actor.avatar_name}
                className={classes.avatarThumbnail}
              />
              {/* Play Button */}
              <button onClick={togglePlay} className={classes.playButton}>
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </button>
              {/* Sound Wave */}
              <div className={classes.soundWave} />
              <div>
                <div style={{ display: "flex" }}>
                  {/* Voice Settings Button */}
                  <Tooltip title="Voice Settings">
                    <IconButton
                      onClick={handleVoiceSettings}
                      style={{
                        color: THEME_COLORS.yellow,
                        background: "none",
                        border: "none",
                        fontSize: "11px",
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      <SettingsVoiceIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Apply custom background">
                    <IconButton
                      onClick={handleBackgroundSettingsClick}
                      sx={{ color: "#FFBF00" }}
                    >
                      <AddPhotoAlternateIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Switch to Enhanced Mode Button */}
                  {isAvatarLabActor && onSwitchToEnhancedMode && (
                    <Tooltip title="Switch to Enhanced mode (Avatar Lab)">
                      <IconButton
                        onClick={handleSwitchToEnhanced}
                        sx={{ color: "#9C27B0" }}
                      >
                        <AutoFixHighIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                {/* Template Button Row - Centered below the other actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <ActorTemplateButton
                    actor={actor}
                    onTemplateChange={handleTemplateChange}
                  />
                  {/* Caption Toggle */}
                  <Tooltip
                    title={
                      actor.caption ? "Disable captions" : "Enable captions"
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleCaptionToggle({
                          target: { checked: !actor.caption },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      color={actor.caption ? "primary" : "default"}
                      sx={{ ml: 1 }}
                      //disable
                    >
                      {actor.caption ? (
                        <ClosedCaptionIcon fontSize="small" />
                      ) : (
                        <ClosedCaptionOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
            {/* Audio Timer */}
            {/*} <div className={classes.audioTimer}>
              {isPlaying ? formatTime(currentTime) : formatTime(duration)}
            </div>*/}
          </div>
        </div>
      </div>

      <div>
        {/* Loading State */}
        {loading && <div>Loading...</div>}

        {/* Voice Settings Panel */}
        {showSettings && (
          <div className={classes.voiceSettingsPanel}>
            {/* Voice Type Selection */}
            <span style={{ fontSize: "11px", color: THEME_COLORS.darkGray }}>
              Choose voice from...
            </span>

            <div style={{ display: "flex" }}>
              <RadioGroup
                value={voiceSelection}
                onChange={(e) =>
                  setVoiceSelection(e.target.value as "heygen" | "elevenLabs")
                }
                row
              >
                <FormControlLabel
                  value="heygen"
                  control={<Radio className={classes.radio} />}
                  label={<span className={classes.label}>Voice Library</span>}
                />
                <Tooltip
                  title={actor.heygenTemplate ? "Currently not available with templates" : ""}
                  placement="top"
                >
                  <FormControlLabel
                    value="elevenLabs"
                    className={classes.radio}
                    control={<Radio className={classes.radio} />}
                    label={
                      <span className={classes.label}>
                        Eleven Labs{" "}
                        {!elevenLabsEnabled ? (
                          <span
                            style={{
                              fontSize: "11px",
                              color: THEME_COLORS.darkGray,
                            }}
                          >
                            (unavailable)
                          </span>
                        ) : actor.heygenTemplate ? (
                          <span
                            style={{
                              fontSize: "11px",
                              color: THEME_COLORS.darkGray,
                            }}
                          >
                            (not available with templates)
                          </span>
                        ) : null}{" "}
                      </span>
                    }
                    disabled={
                      !elevenLabsEnabled || !canUseFeatures(currentWorkspace) || !!actor.heygenTemplate
                    }
                  />
                </Tooltip>
              </RadioGroup>
            </div>

            {/* Voice Settings */}
            {voiceSelection === "elevenLabs" && (
              <>
                <div className={classes.settingsRow}>
                  <span>Voice: </span>
                  <button
                    className={classes.voiceButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setElevenLabsVoiceModalOpen(true);
                    }}
                  >
                    {selectedVoiceId
                      ? customVoices.find(
                          (voice) => voice.voice_id === selectedVoiceId
                        )?.name
                      : "Select Voice"}
                  </button>

                  <ElevenLabsVoicesDialog
                    open={elevenLabsVoiceModalOpen}
                    onClose={() => setElevenLabsVoiceModalOpen(false)}
                    elevenLabsVoices={customVoices}
                    onSelectVoice={async (voiceId) =>
                      await handleAddCustomVoice(voiceId)
                    }
                  />
                </div>

                {/* Settings for the Selected Voice */}
                {selectedVoiceId && (
                  <div className={classes.settings}>
                    <div className={classes.settingsRow}>
                      <span>Stability</span>
                      <Slider
                        value={voiceSettings?.stability}
                        onChange={(e, value) =>
                          handleSettingChange("stability", value)
                        }
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </div>
                    <div className={classes.settingsRow}>
                      <span>Similarity</span>
                      <Slider
                        value={voiceSettings?.similarity_boost}
                        onChange={(e, value) =>
                          handleSettingChange("similarity_boost", value)
                        }
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </div>
                    <div className={classes.settingsRow}>
                      <span>Style</span>
                      <Slider
                        value={voiceSettings?.style}
                        onChange={(e, value) =>
                          handleSettingChange("style", value)
                        }
                        min={0}
                        max={1}
                        step={0.1}
                      />
                    </div>
                    <div className={classes.settingsRow}>
                      <span>Speaker Boost</span>
                      <Switch
                        checked={voiceSettings?.use_speaker_boost}
                        onChange={(e) =>
                          handleSettingChange(
                            "use_speaker_boost",
                            e.target.checked
                          )
                        }
                      />
                    </div>
                    <div className={classes.settingsRow}>
                      <span>Speed</span>
                      <Slider
                        value={voiceSettings?.speed || 1.0}
                        onChange={(e, value) =>
                          handleSettingChange("speed", value)
                        }
                        min={0.7} // Changed from 0.5
                        max={1.2} // Changed from 2.0
                        step={0.1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {voiceSelection === "heygen" && (
              <>
                <div className={classes.settingsRow}>
                  <span>Voice: </span>
                  <button
                    className={classes.voiceButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setHeygenVoiceModalOpen(true);
                    }}
                  >
                    {selectedHeygenVoiceId
                      ? heygenVoices.find(
                          (voice) => voice.voice_id === selectedHeygenVoiceId
                        )?.name ||
                        heygenCustomVoices.find(
                          (voice) => voice.voice_id === selectedHeygenVoiceId
                        )?.name ||
                        "Select Voice"
                      : "Select Voice"}
                  </button>

                  <VoiceSelectionModal
                    open={heygenVoiceModalOpen}
                    onClose={() => setHeygenVoiceModalOpen(false)}
                    heygenVoices={heygenVoices}
                    onSelectVoice={async (voiceId, meta) =>
                      await handleAddHeygenVoice(voiceId, meta)
                    }
                    actorId={actor.avatar_id}
                    actorName={actor.avatar_name}
                    customVoices={heygenCustomVoices}
                    customVoicesLoading={heygenCustomVoicesLoading}
                    onRefreshCustomVoices={fetchHeygenCustomVoices}
                  />
                </div>

                {/* Heygen Voice Settings */}
                {selectedHeygenVoiceId &&
                  actor.voice_settings?.type === "text" && (
                    <div className={classes.settings}>
                      <div className={classes.settingsRow}>
                        <span>Speed</span>
                        <Slider
                          value={
                            (actor.voice_settings as TextVoiceSettings).speed ||
                            1.0
                          }
                          onChange={(e, value) =>
                            handleHeygenSettingChange("speed", value as number)
                          }
                          min={0.5}
                          max={1.5}
                          step={0.1}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                        />
                      </div>

                      <div className={classes.settingsRow}>
                        <span>Pitch</span>
                        <Slider
                          value={
                            (actor.voice_settings as TextVoiceSettings).pitch ||
                            0
                          }
                          onChange={(e, value) =>
                            handleHeygenSettingChange("pitch", value as number)
                          }
                          min={-50}
                          max={50}
                          step={1}
                          valueLabelDisplay="auto"
                        />
                      </div>

                      <div className={classes.settingsRow}>
                        <span>Emotion</span>
                        <Select
                          value={
                            (actor.voice_settings as TextVoiceSettings)
                              .emotion || ""
                          }
                          onChange={(e) =>
                            handleHeygenSettingChange(
                              "emotion",
                              e.target.value as VoiceEmotion
                            )
                          }
                          displayEmpty
                          style={{
                            fontSize: "12px",
                            width: "100%",
                            maxHeight: "30px",
                            marginLeft: "8px",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Emotion
                          </MenuItem>
                          <MenuItem value="Excited">Excited</MenuItem>
                          <MenuItem value="Friendly">Friendly</MenuItem>
                          <MenuItem value="Serious">Serious</MenuItem>
                          <MenuItem value="Soothing">Soothing</MenuItem>
                          <MenuItem value="Broadcaster">Broadcaster</MenuItem>
                        </Select>
                      </div>

                      <div
                        className={classes.settingsRow}
                        style={{ marginTop: "8px" }}
                      >
                        <span>Locale</span>
                        <Select
                          value={
                            (actor.voice_settings as TextVoiceSettings)
                              .locale || ""
                          }
                          onChange={(e) =>
                            handleHeygenSettingChange("locale", e.target.value)
                          }
                          displayEmpty
                          style={{
                            fontSize: "12px",
                            width: "100%",
                            maxHeight: "30px",
                            marginLeft: "8px",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Locale
                          </MenuItem>
                          {availableLocales.map((locale) => (
                            <MenuItem key={locale.value} value={locale.value}>
                              {locale.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleHearPreview}
                        disabled={isLoadingPreview || !selectedHeygenVoiceId}
                        startIcon={
                          isLoadingPreview ? (
                            <CircularProgress size={16} />
                          ) : (
                            <VolumeUpIcon fontSize="small" />
                          )
                        }
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontSize: "12px",
                          borderColor: THEME_COLORS.yellow,
                          color: THEME_COLORS.darkYellow,
                          "&:hover": {
                            borderColor: THEME_COLORS.darkYellow,
                            backgroundColor: "rgba(255, 191, 0, 0.1)",
                          },
                          "&.Mui-disabled": {
                            borderColor: "#ccc",
                            color: "#999",
                          },
                        }}
                      >
                        {isLoadingPreview
                          ? "Generating preview..."
                          : "Hear Audio Preview"}
                      </Button>

                      {/* ElevenLabs Settings (if using ElevenLabs through Heygen) */}
                      {(actor.voice_settings as TextVoiceSettings)
                        .elevenlabs_settings && (
                        <>
                          <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            ElevenLabs Advanced Settings
                          </Typography>
                          <div className={classes.settingsRow}>
                            <span>Stability</span>
                            <Slider
                              value={
                                (actor.voice_settings as TextVoiceSettings)
                                  .elevenlabs_settings?.stability || 0.75
                              }
                              onChange={(e, value) =>
                                handleHeygenElevenLabsSettingChange(
                                  "stability",
                                  value as number
                                )
                              }
                              min={0}
                              max={1}
                              step={0.05}
                            />
                          </div>
                          <div className={classes.settingsRow}>
                            <span>Similarity Boost</span>
                            <Slider
                              value={
                                (actor.voice_settings as TextVoiceSettings)
                                  .elevenlabs_settings?.similarity_boost || 0.5
                              }
                              onChange={(e, value) =>
                                handleHeygenElevenLabsSettingChange(
                                  "similarity_boost",
                                  value as number
                                )
                              }
                              min={0}
                              max={1}
                              step={0.05}
                            />
                          </div>
                          <div className={classes.settingsRow}>
                            <span>Style</span>
                            <Slider
                              value={
                                (actor.voice_settings as TextVoiceSettings)
                                  .elevenlabs_settings?.style || 0
                              }
                              onChange={(e, value) =>
                                handleHeygenElevenLabsSettingChange(
                                  "style",
                                  value as number
                                )
                              }
                              min={0}
                              max={1}
                              step={0.05}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Audio Element */}
      <audio
        ref={audioRef}
        src={audioSrc}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }} // Hide default audio controls
      />
      {/* Background Settings Dialog */}
      {currentWorkspace && videoPageId && (
        <BackgroundSettingsDialog
          open={isBackgroundSettingsOpen}
          onClose={() => setIsBackgroundSettingsOpen(false)}
          actorId={actor._id}
          workspaceId={currentWorkspace._id}
          videoPageId={videoPageId}
          currentScale={actor?.avatarScale || 1.0}
          currentBackground={actor?.backgroundAsset || null}
          dimension={dimension}
          onSave={handleSaveBackgroundSettings}
          actor={actor}
        />
      )}
      {actor?.isTalkingPhoto &&
        !actor.heygenVoiceId &&
        !actor.heygenVoiceId && (
          <Message
            text="This avatar requires a voice selection - choose one in Voice Settings"
            type="hint"
          />
        )}
    </div>
  );
};

export default VoicePlayer;
